import { FileText, Receipt } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsg } from '@/i18n/msg';
import { cn } from '@/lib/utils';
import { RowActions, StatusChip } from '@/components/editorial';
import {
  claimDateLabel,
  expenseCategoryToken,
  formatUsd,
  ledgerStatus,
  scanFailed,
} from '@/lib/bills-display';
import type { ReimbursementClaim } from '@/types/api';
import { useReceiptThumbnail } from './useReceiptThumbnail';

/**
 * One expense, one line — the ledger row (Finances collector spec 2026-09-10
 * §4.3), the `TaskRow` idiom applied to money. Borrowed from the shape BILL
 * Spend & Expense, Expensify and a bank statement converge on: the receipt
 * (or the dashed square where one is missing — BILL's "Incomplete" cue), the
 * merchant, the facts a parent scans for in small type — category, date,
 * what it was for — then the amount right-aligned in tabular digits, the
 * status, and the row actions.
 *
 * The row never invents a value: an amount the scan could not read is a dash,
 * a receipt the scan could not read says so in the facts line.
 */
export interface ExpenseRowProps {
  claim: ReimbursementClaim;
  /** Opens the details dialog (read-only once the record is with TRF). */
  onOpen: (claim: ReimbursementClaim) => void;
  /** The pencil — only while the record is the family's (draft, needs_info). */
  onEdit?: (claim: ReimbursementClaim) => void;
  /** The trash — drafts only; the page asks first (ConfirmActionDialog). */
  onDelete?: (claim: ReimbursementClaim) => void;
  now?: Date;
}

const FAMILY_EDITABLE = new Set(['draft', 'needs_info']);

export function ExpenseRow({ claim, onOpen, onEdit, onDelete, now = new Date() }: ExpenseRowProps) {
  const { t } = useTranslation(['money', 'common']);
  const resolve = useMsg();
  const status = ledgerStatus(claim, now);
  const category = claim.category ? expenseCategoryToken(claim.category) : null;
  const dateText = claimDateLabel(claim, now);
  const thumb = useReceiptThumbnail(claim.source_document_id, claim.document?.mime_type ?? null);
  const isPdf = claim.document?.mime_type === 'application/pdf';
  const title = claim.vendor || (status.key === 'scanning' ? t('row.reading') : t('row.receipt'));
  const editable = FAMILY_EDITABLE.has(claim.status);
  const failed = scanFailed(claim) && claim.status === 'draft';

  return (
    <li
      data-testid="expense-row"
      data-claim-id={claim._id}
      data-claim-status={claim.status}
      className="group relative flex items-start gap-3 rounded-md px-2 py-2 -mx-2 hover:bg-muted/40 transition-colors"
    >
      {/* The receipt slot. A dashed square is the one visual cue the row is
          incomplete; the chip says the same thing in words. */}
      <div
        className={cn(
          'mt-0.5 h-10 w-10 shrink-0 overflow-hidden rounded-md border flex items-center justify-center',
          claim.source_document_id ? 'border-border/60 bg-muted/30' : 'border-dashed border-status-warn-border',
        )}
        aria-hidden="true"
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        ) : claim.source_document_id ? (
          <FileText className={cn('h-4 w-4 text-stevens-ink-3', !isPdf && status.key === 'scanning' && 'animate-pulse')} />
        ) : (
          <Receipt className="h-4 w-4 text-status-warn-fg" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <button
            type="button"
            onClick={() => onOpen(claim)}
            className={cn(
              'min-w-0 truncate text-left text-body font-medium text-foreground hover:underline underline-offset-2',
              status.key === 'scanning' && 'text-stevens-ink-3 font-normal',
            )}
          >
            {title}
          </button>
        </div>
        {/* #2381: each `·` rides in one box with the item it introduces, so
            a wrap never leaves a dot dangling at the end of a line. #2496:
            and a line never STARTS with one. Every fact carries its leading
            dot, and the facts sit one dot slot (15 px) left of the line
            inside a clipping box, so the first fact on each line — the
            first fact, or one that wrapped — has its dot cut off. The status
            chip's phone copy leads the line, because the right-hand chip is
            hidden below sm and the status is what a parent checks first. */}
        <p className="overflow-hidden text-caption text-stevens-ink-3" data-testid="expense-facts">
          <span className="-ms-[15px] flex flex-wrap items-center gap-y-1">
            <Fact className="sm:hidden">
              <StatusChip tone={status.tone} data-testid="expense-status-mobile" className="sm:hidden">
                {resolve({ key: status.labelKey })}
              </StatusChip>
            </Fact>
            {category && (
              <Fact>
                <span className="inline-flex items-center gap-1">
                  <span className={cn('inline-block h-[7px] w-[7px] shrink-0 rounded-full', category.rail)} />
                  {resolve({ key: category.labelKey })}
                </span>
              </Fact>
            )}
            {dateText && (
              <Fact>
                <span>{dateText}</span>
              </Fact>
            )}
            {claim.note && (
              <Fact className="min-w-0 max-w-full">
                <span className="truncate">{claim.note}</span>
              </Fact>
            )}
            {failed && (
              <Fact>
                <span className="text-status-warn-fg">{t('row.scanFailed')}</span>
              </Fact>
            )}
            {!category && !dateText && !claim.note && !failed && status.key === 'needs_details' && (
              <Fact>
                <span className="text-status-warn-fg">{t('row.needsDetails')}</span>
              </Fact>
            )}
          </span>
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span
          className={cn('w-24 text-right font-mono tabular-nums text-body', claim.amount == null && 'text-stevens-ink-3')}
          data-testid="expense-amount"
        >
          {formatUsd(claim.amount)}
        </span>
        <StatusChip tone={status.tone} data-testid="expense-status" className="hidden sm:inline-flex">
          {resolve({ key: status.labelKey })}
        </StatusChip>
        <RowActions
          editLabel={t('row.edit')}
          deleteLabel={t('row.delete')}
          onEdit={editable && onEdit ? () => onEdit(claim) : undefined}
          onDelete={claim.status === 'draft' && onDelete ? () => onDelete(claim) : undefined}
        />
      </div>
    </li>
  );
}

/**
 * One fact in the row's facts line, led by the `·` that introduces it in the
 * same box (#2381), so a wrap never strands a dot at the end of a line. The
 * dot sits in a fixed 15 px slot (about the old 6 px gap + glyph + 6 px
 * gap); the line is shifted left by exactly one slot inside a clipping box,
 * so whichever fact starts a line has its dot clipped and no line starts
 * with a dot (#2496).
 */
function Fact({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center', className)}>
      <span aria-hidden="true" data-fact-dot="" className="w-[15px] shrink-0 text-center">·</span>
      {children}
    </span>
  );
}
